import React, { useMemo, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import classes from './OrderSummaryModal.module.css';
import Modal from '../../../components/UI/Modals/Modal';
import { ICustomer } from '../../../store/slices/customers';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { ICartProduct, selectCart } from '../../../store/slices/cart';
import { DataTable } from '../../../components/DataTable/DataTable';
import { getPrintableHtml, getTextByLanguage } from '../../../utils/miscUtils';
import downloadIcon from '../../../assets/icons/download-icon.svg';
import printIcon from '../../../assets/icons/print-icon.svg';
import { ISalesOrderProduct } from '../../../store/slices/salesOrderProducts';
import moment from 'moment';
import { IAccountInfo, selectAccountInfo, selectUserLocation } from '../../../store/slices/auth';
import { salesOrderStates } from '../../../store/slices/checkIn';
import { useTranslation } from '../../../hooks/useTranslations';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  customer: ICustomer;
  // used only from Orders table if you want to see the order summary
  cartItems?: {
    name: string;
    quantity: number;
    vat: number;
    total: number;
  }[];
  salesOrder?: ISalesOrderProduct;
};

type DataTableItem = {
  name: string;
  quantity: number;
  vat: number;
  total: number;
  salesOrderId?: number | string;
  salesOrderExternalId?: number | string;
  salesOrderItemId?: number | string;
};

type DataTableFooter = {
  total: number | string;
};

const OrderSummaryModal: React.FC<Props> = ({
  isOpen,
  onClose,
  customer,
  cartItems,
  salesOrder,
}) => {
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();
  const lang = localStorage.getItem('lang') || 'en';
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const carts = useAppSelector<ICartProduct[]>(selectCart);
  const [hideDownload, setHideDownload] = React.useState<boolean>(false);
  const componentPrintRef = useRef<HTMLInputElement>(null);
  const userLocation = useAppSelector<string>(selectUserLocation);
  const totalSum = useMemo(() => {
    return carts.reduce((sum, product) => sum + (product.product.price || 0) * product.quantity, 0);
  }, [carts]);
  const handlePrint = useReactToPrint({
    content: () => componentPrintRef.current,
  });
  React.useEffect(() => {
    dispatch(
      salesOrderStates({
        accountNumber: accountInfo.account_number,
        personAccount: customer?.person_account_number,
      })
    );
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      containerPadding={'20px'}
      width={'80%'}
      maxWidth={'850px'}
      headerStyles={{ width: '100%' }}
    >
      <div className={classes.container}>
        <div className={classes.leftContainer} id={'divToPrint'}>
          <div className={classes.headerContainer}>
            <div className={classes.mainHeader}>{translations('order.summary')}</div>
            <div className={classes.headerRightContainer}>
              {salesOrder && !hideDownload && (
                <div className={classes.printButton} onClick={handlePrint}>
                  {translations('print')}
                  <img src={printIcon} className={classes.printIcon} alt="printIcon" />
                </div>
              )}
              {!hideDownload && (
                <div
                  className={classes.downloadButton}
                  onClick={() => {
                    setHideDownload(true);
                    setTimeout(() => {
                      const htmlContent = document.getElementById('divToPrint')?.innerHTML;
                      const printable = (
                        document.getElementById('ifmContentsToPrint') as HTMLIFrameElement
                      ).contentWindow;
                      if (printable) {
                        const originalTitle = document.title;
                        document.title = translations('order.summary').toString();
                        printable.document.open();
                        printable.document.write(
                          getPrintableHtml(htmlContent ?? '', Array.from(document.styleSheets))
                        );
                        printable.document.close();
                        printable.focus();
                        printable.print();
                        document.title = originalTitle;
                      }
                      setHideDownload(false);
                    }, 100);
                  }}
                >
                  <div className={classes.downloadText}>{translations('download')}</div>
                  <img src={downloadIcon} alt="download" />
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'none' }}>
            <div ref={componentPrintRef} className={classes.printContainer}>
              <div className={classes.printHeader}>
                <div style={{ textTransform: 'uppercase' }}>{translations('customer.receipt')}</div>
                <div>{userLocation}</div>
              </div>
              <div className={classes.printOrderContainer}>
                <div>{`${translations('label.order_id')}: ${salesOrder?.sales_order_id}`}</div>
                <div>
                  {moment(salesOrder ? salesOrder.datetime_create : Date.now()).format(
                    'DD.MM.yyyy, HH:mm'
                  )}
                </div>
              </div>
              <div className={classes.printTable}>
                <div className={classes.printTableHeader}>
                  <div style={{ flex: 2 }}>{translations('product')}</div>
                  <div style={{ flex: 1 }}>{translations('quantity')}</div>
                  <div style={{ flex: 1 }}>{translations('price')}</div>
                </div>
                {cartItems?.map((item, index) => (
                  <div key={index} className={classes.printTableRow}>
                    <div style={{ flex: 2 }}>{item?.name}</div>
                    <div style={{ flex: 1 }}>1</div>
                    <div style={{ flex: 1 }}>
                      €{item?.total ? parseFloat(item?.total.toString()).toFixed(2) : 0}
                    </div>
                  </div>
                ))}
              </div>
              <div className={classes.printTotal}>{`${translations('total')}: €${
                salesOrder
                  ? ((salesOrder.amount / 100) * 100).toFixed(2)
                  : (totalSum / 100).toFixed(2)
              }`}</div>
              <div className={classes.printFooter}>
                <div>{`${customer.person_firstname} ${customer.person_lastname}`}</div>
                <div style={{ textTransform: 'uppercase' }}>{translations('customer.copy')}</div>
                <div style={{ textTransform: 'uppercase' }}>
                  {translations('thanks.for.visiting')}
                </div>
              </div>
            </div>
          </div>
          <div className={classes.header}>{translations('order.summary.customer')}</div>
          <div className={classes.fieldsContainer}>
            <div className={classes.rowContainer}>
              <div className={classes.field}>
                {/* <CustomTextField
                  value={customer.person_firstname}
                  onChange={() => {}}
                  label={translations('first_name').toString()}
                /> */}
                <div className={classes.labelAlternate}>
                  {translations('first_name').toString()}{' '}
                </div>
                <div className={classes.orderDetails}>{customer.person_firstname}</div>
              </div>
              <div className={classes.field}>
                {/* <CustomTextField
                  value={customer.person_lastname}
                  onChange={() => {}}
                  label={translations('last_name').toString()}
                /> */}
                <div className={classes.labelAlternate}>{translations('last_name').toString()}</div>
                <div className={classes.orderDetails}>{customer.person_lastname}</div>
              </div>
            </div>
            <div className={classes.rowContainer}>
              <div className={classes.field}>
                {/* <CustomTextField
                  value={customer.person_email}
                  onChange={() => {}}
                  label={translations('email').toString()}
                /> */}
                <div className={classes.labelAlternate}>{translations('email').toString()}</div>
                <div className={classes.orderDetails}>{customer.person_email}</div>
              </div>
              <div className={classes.field}>
                {/* <CustomTextField
                  value={customer.person_mobile_phone}
                  onChange={() => {}}
                  label={translations('phone.number').toString()}
                /> */}
                <div className={classes.labelAlternate}>
                  {translations('phone.number').toString()}
                </div>
                <div className={classes.orderDetails}>{customer.person_mobile_phone}</div>
              </div>
            </div>
            <div className={classes.rowContainer}>
              <div className={classes.field}>
                {/* <CustomTextField
                  value={customer.street ?? ''}
                  onChange={() => {}}
                  label={translations('address').toString()}
                /> */}
                <div className={classes.labelAlternate}>{translations('address').toString()}</div>
                <div className={classes.orderDetails}>{customer.street ?? ''}</div>
              </div>
              <div className={classes.field}>
                {/* <CustomTextField
                  value={customer.postcode?.toString() ?? ''}
                  onChange={() => {}}
                  label={translations('postal_code').toString()}
                /> */}
                <div className={classes.labelAlternate}>
                  {translations('postal_code').toString()}
                </div>
                <div className={classes.orderDetails}>{customer.postcode?.toString() ?? ''}</div>
              </div>
            </div>
            <div className={classes.rowContainer}>
              <div className={classes.field}>
                {/* <CustomTextField
                  value={customer.city ?? ''}
                  onChange={() => {}}
                  label={translations('city').toString()}
                /> */}
                <div className={classes.labelAlternate}>{translations('city').toString()}</div>
                <div className={classes.orderDetails}>{customer.city ?? ''}</div>
              </div>
              <div className={classes.field}>
                {/* <CustomTextField
                  value={customer.country ?? ''}
                  onChange={() => {}}
                  label={translations('country').toString()}
                /> */}
                <div className={classes.labelAlternate}>{translations('country').toString()}</div>
                <div className={classes.orderDetails}>{customer.country ?? ''}</div>
              </div>
            </div>
          </div>
          {salesOrder ? (
            <div className={classes.orderContainer}>
              <div className={classes.label}>{translations('order.details')}</div>
              <div className={classes.labelAndValue}>
                <div className={classes.labelAlternate}>{translations('order.id')}</div>
                <div>{salesOrder.sales_order_id}</div>
              </div>
              <div className={classes.labelAndValue}>
                <div className={classes.labelAlternate}>{translations('date.time')}</div>
                <div>{moment(salesOrder.datetime_create).format('DD.MM.yyyy, HH:mm')}</div>
              </div>
            </div>
          ) : (
            <div className={classes.dateTimeContainer}>
              <div className={classes.header}>{translations('order.date.time')}</div>{' '}
              <div className={classes.dateTimeHeader}>
                {moment(Date.now()).format('DD.MM.yyyy, HH:mm')}
              </div>
            </div>
          )}
          <div className={classes.tableContainer}>
            {!salesOrder && <div className={classes.header}>{translations('details')}</div>}
            <DataTable<DataTableItem, DataTableFooter>
              simple
              isDownloading={hideDownload}
              rows={
                cartItems
                  ? cartItems
                  : carts.map((cart) => ({
                      name: getTextByLanguage(cart.product.name, lang),
                      quantity: cart.quantity,
                      vat: 19,
                      total: cart.product.price,
                    }))
              }
              cellStyle={{
                padding: '20px 10px',
              }}
              columns={[
                {
                  displayName: translations('doctor.id'),
                  renderer: (row) =>
                    salesOrder
                      ? `${row.salesOrderId}/${row.salesOrderExternalId}/${row.salesOrderItemId}`
                      : '',
                },
                {
                  displayName: translations('item'),
                  renderer: (row) => row.name,
                },
                {
                  displayName: translations('quantity'),
                  align: 'center',
                  renderer: (row) => row.quantity,
                },
                {
                  displayName: translations('Vat'),
                  align: 'center',
                  renderer: (row) => <>{row.vat} %</>,
                },
                {
                  displayName: translations('total'),
                  align: 'right',
                  renderer: (row) => <>€ {(cartItems ? row.total : row.total / 100).toFixed(2)}</>,
                },
              ]}
              footer={{
                columns: [
                  {
                    displayName: translations('vat'),
                    renderer: () => '',
                  },
                  {
                    displayName: translations('vat'),
                    renderer: () => '',
                  },
                  {
                    displayName: translations('vat'),
                    renderer: () => '',
                  },
                  {
                    displayName: '',
                    renderer: () => '',
                  },
                  {
                    displayName: translations('vat'),
                    align: 'right',
                    renderer: () => (
                      <div className={classes.total}>
                        <span style={{ marginRight: 5 }}>{translations('total')}</span> €
                        {salesOrder
                          ? ((salesOrder.amount / 100) * 100).toFixed(2)
                          : (totalSum / 100).toFixed(2)}
                      </div>
                    ),
                  },
                ],
                data: {
                  total: (totalSum / 100).toFixed(2),
                },
              }}
            />
          </div>
        </div>
      </div>
      <iframe
        title={translations('printable').toString()}
        id="ifmContentsToPrint"
        style={{ height: 0, width: 0, position: 'absolute' }}
      />
    </Modal>
  );
};

export { OrderSummaryModal };
